import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Radio, Clock, MapPin } from 'lucide-react';
import { buildEvent } from '../lib/calendar';

const fmtTime = (d) => d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' });

const allEvents = (agenda) => agenda.flatMap((d) => d.items.map((it) => ({ ...buildEvent(d, it), item: it, day: d.day })));

function Row({ ev, live }) {
  if (!live) {
    return (
      <li className="py-4" data-testid="up-next-item">
        <div className="font-heading text-lg md:text-2xl tracking-widest text-gold">{fmtTime(ev.start)} – {fmtTime(ev.end)}</div>
        <div className="font-display text-3xl md:text-5xl text-cbtu-cream leading-tight mt-1">{ev.item.title}</div>
        {ev.item.room && <div className="text-cbtu-text/90 text-lg md:text-2xl inline-flex items-center gap-2 mt-2"><MapPin size={22} className="text-gold" /> {ev.item.room}</div>}
      </li>
    );
  }
  return (
    <li className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2" data-testid="happening-now-item">
      <span className="font-heading text-xs tracking-widest text-gold whitespace-nowrap min-w-[150px]">{fmtTime(ev.start)} – {fmtTime(ev.end)}</span>
      <span className="text-cbtu-cream font-medium">{ev.item.title}</span>
      {ev.item.room && <span className="text-cbtu-text/80 text-sm inline-flex items-center gap-1"><MapPin size={13} className="text-gold" /> {ev.item.room}</span>}
    </li>
  );
}

export const HappeningNow = ({ agenda }) => {
  const [params] = useSearchParams();
  const override = params.get('now');
  const [now, setNow] = useState(() => (override ? new Date(override) : new Date()));
  useEffect(() => {
    if (override) return undefined;
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, [override]);

  const events = allEvents(agenda);
  const first = events[0]?.start;
  const last = events.reduce((m, e) => (e.end > m ? e.end : m), first);
  if (!first || now < new Date(first.getTime() - 12 * 3600 * 1000) || now > last) return null;

  const live = events.filter((e) => e.start <= now && now < e.end);
  const nextStart = events.filter((e) => e.start > now).map((e) => e.start.getTime()).sort((a, b) => a - b)[0];
  const next = nextStart ? events.filter((e) => e.start.getTime() === nextStart) : [];

  return (
    <section className="max-w-7xl mx-auto px-4 pt-10" data-testid="happening-now">
      <div className="rounded-md border-2 border-gold/70 bg-cbtu-green/30 p-6 md:p-8">
        {live.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 font-heading tracking-widest text-sm text-red-300 mb-2">
              <span className="relative flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" /><span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" /></span>
              <Radio size={15} /> HAPPENING NOW
            </div>
            <ul className="divide-y divide-gold/15">{live.map((e) => <Row key={`${e.day}-${e.item.title}`} ev={e} live />)}</ul>
          </div>
        )}
        {next.length > 0 && (
          <div>
            <div className="flex items-center gap-3 font-heading tracking-widest text-xl md:text-3xl text-gold mb-1" data-testid="up-next-heading"><Clock size={28} /> UP NEXT{live.length === 0 && next[0].day ? ` · ${next[0].day.toUpperCase()}` : ''}</div>
            <ul className="divide-y divide-gold/15">{next.map((e) => <Row key={`${e.day}-${e.item.title}`} ev={e} />)}</ul>
          </div>
        )}
      </div>
    </section>
  );
};
